import { useState } from "react";
import { useUser } from "@/context/UserContext";
import {
  saveAccountWallet,
  removeAccountWallet,
  friendlyApiError,
} from "@/lib/api";
import { isValidAddress } from "@/lib/wallet";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Pencil, Trash2 } from "lucide-react";

const truncate = (s) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : "");

/**
 * Wallet address attached to the authenticated X/Osteon account.
 * Paste-and-save only: no connection, no signature, no ownership proof.
 */
export default function WalletSection() {
  const { user, refresh } = useUser();
  const [pasted, setPasted] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(null); // save | remove

  if (!user) return null;

  const address = user.wallet_address || null;

  const reload = async () => {
    await refresh().catch(() => toast.error("Saved, but the profile did not reload."));
  };

  const failToast = (err, fallback) => {
    const code = err.response?.data?.detail;
    if (code === "WALLET_ALREADY_LINKED") {
      toast.error("That wallet already belongs to another Osteon account.");
    } else if (err.response?.status === 400) {
      toast.error("That is not a valid EVM wallet address.");
    } else {
      toast.error(friendlyApiError(err, fallback));
    }
  };

  const onSave = async (e) => {
    e?.preventDefault();
    const candidate = pasted.trim();
    if (!isValidAddress(candidate)) {
      toast.error("Enter a valid 0x wallet address (40 hex characters).");
      return;
    }
    setBusy("save");
    try {
      await saveAccountWallet(candidate);
      setPasted("");
      setShowForm(false);
      await reload();
      toast.success("Wallet saved");
    } catch (err) {
      failToast(err, "Unable to save this wallet. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const onRemove = async () => {
    setBusy("remove");
    try {
      await removeAccountWallet();
      setShowForm(false);
      await reload();
      toast.success("Wallet removed");
    } catch {
      toast.error("Unable to remove this wallet. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section id="wallet" className="relative py-16 border-t border-[#1b2234]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="font-mono-cyber text-[11px] uppercase tracking-[0.35em] text-[#00f0ff] mb-4">// Wallet</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight">
              Your <span className="text-[#00f0ff]">wallet</span>.
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              One wallet address per account. Paste it below and save — it is stored
              on your account as the destination address.
            </p>
          </div>

          <div className="lg:col-span-7" data-testid="wallet-section">
            <div className="cyber-corners p-6 sm:p-8 bg-[#0d1017]/70 border border-[#1b2234] clip-terminal space-y-5">
              {(!address || showForm) && (
                <form onSubmit={onSave} className="space-y-4">
                  <div>
                    <Label className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-[#00f0ff]">
                      Wallet address
                    </Label>
                    <Input
                      data-testid="wallet-paste-input"
                      value={pasted}
                      onChange={(e) => setPasted(e.target.value.trim())}
                      placeholder="0x…"
                      spellCheck={false}
                      autoComplete="off"
                      className="mt-2 bg-[#07080c] border-[#1b2234] rounded-none h-12 font-mono-cyber text-sm"
                    />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="submit"
                      disabled={busy === "save"}
                      data-testid="wallet-save-btn"
                      className="rounded-none clip-notch h-12 px-8 bg-[#00f0ff] hover:bg-[#00f0ff]/90 text-black font-mono-cyber uppercase tracking-widest text-xs"
                    >
                      {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Wallet"}
                    </Button>
                    {address && (
                      <Button
                        type="button"
                        onClick={() => { setShowForm(false); setPasted(""); }}
                        className="rounded-none clip-notch h-12 px-8 bg-transparent border border-[#1b2234] hover:bg-[#1b2234] text-slate-300 font-mono-cyber uppercase tracking-widest text-xs"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              )}

              {address && !showForm && (
                <>
                  <div className="font-mono-cyber text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Wallet
                  </div>
                  <div className="font-mono-cyber text-sm text-slate-100" data-testid="wallet-address">
                    {truncate(address)}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => { setShowForm(true); setPasted(""); }}
                      data-testid="wallet-change-btn"
                      className="rounded-none clip-notch h-11 px-6 bg-transparent border border-[#1b2234] hover:bg-[#1b2234] text-slate-100 font-mono-cyber uppercase tracking-widest text-[11px]"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Change Wallet
                    </Button>
                    <Button
                      onClick={onRemove}
                      disabled={busy === "remove"}
                      data-testid="wallet-remove-btn"
                      className="rounded-none clip-notch h-11 px-6 bg-transparent border border-[#1b2234] hover:border-[#ff6b6b]/60 hover:text-[#ff6b6b] text-slate-400 font-mono-cyber uppercase tracking-widest text-[11px]"
                    >
                      {busy === "remove" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5 mr-2" /> Remove</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
